import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatusBadge from '../../components/StatusBadge.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { AvatarGroup } from '../../components/Avatar.jsx';
import { TasksAPI } from '../../lib/api';

import { notify } from '../../lib/notifications';
import { useAuth } from '../../lib/auth.jsx';
import SortableTh from '../../components/SortableTh.jsx';
import { nextSortState, sortRows } from '../../lib/tableSort.js';
import {
  buildPath,
  statusMeta,
  TASK_FILTERS,
  TASK_STATUS,
  isIndividualUser,
} from '../../config';
import {
  clubEndLabel,
  clubMetaLabel,
  clubRoutineRows,
  occurrenceCaption,
  uniformValue,
  unionAssignees,
} from '../../lib/routineClub.js';

// Page-title labels per filter so the header reflects what the user is
// actually looking at.  Keys match ``TASK_FILTERS[].value`` in
// ``config.js`` (and the cron / v2 HTML view's ``filter`` kwarg).
const FILTER_TITLES = {
  my_tasks:         'My Tasks',
  my_team_tasks:    'My Team Tasks',
  my_created_tasks: 'Tasks By Me',
};

// Order in which the bifurcated status tables are rendered.  Mirrors the
// order requested by the product brief and the server-rendered
// ``templates/task_management/v2/task-list.html`` layout:
//   In Progress → Awaiting Feedback → Assigned → Testing → Completed.
const STATUS_ORDER = [
  'in_progress',
  'waiting_for_feedback',
  'assigned',
  'testing',
  'completed',
];

// "Fast" one-click status transitions exposed as a tick-box button:
//   * Assigned    → In Progress
//   * In Progress → Completed
// Every other status is changed via the Edit screen (per the brief:
// "for other statuses the user will edit and do the needful").
const QUICK_NEXT = {
  assigned:    'in_progress',
  in_progress: 'completed',
};

function clubDomId(key) {
  let hash = 0;
  const text = String(key);
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash * 31) + text.charCodeAt(i)) >>> 0;
  }
  return `routine-club-${hash.toString(36)}`;
}

function TaskDataRow({
  task,
  child = false,
  cadence = null,
  rowId,
  quickTarget,
  tickModifier,
  busyId,
  onQuickAdvance,
}) {
  const caption = child ? occurrenceCaption(task, cadence) : '';
  return (
    <tr
      id={rowId}
      className={child ? 'ty-routine-child' : undefined}
      data-testid={child ? 'routine-club-day' : undefined}
    >
      <td data-label="" className="ty-tick-col">
        {quickTarget && (
          <button
            type="button"
            className={`ty-tick ${tickModifier}`}
            title={`Mark as ${statusMeta('task', quickTarget).label}`}
            aria-label={`Mark “${task.title}” as ${statusMeta('task', quickTarget).label}`}
            data-testid={child ? 'routine-day-tick' : 'task-tick'}
            disabled={busyId === task.id}
            onClick={() => onQuickAdvance(task)}
          >
            {busyId === task.id ? '…' : '✓'}
          </button>
        )}
      </td>
      <td data-label="Title">
        <Link to={buildPath.taskDetails(task.id)}>{task.title}</Link>
        {caption ? <span className="ty-routine-meta">{caption}</span> : null}
      </td>
      <td data-label="Assigned To">
        {task.assignees && task.assignees.length > 0 ? (
          <AvatarGroup people={task.assignees} size={26} max={4} />
        ) : (
          <span style={{ color: 'var(--ty-muted)' }}>—</span>
        )}
      </td>
      <td data-label="Urgency">{task.urgency}</td>
      <td data-label="End">{task.end_date || '—'}</td>
      <td data-label="Status">
        <StatusBadge domain="task" value={task.status} />
      </td>
      <td data-label="" className="ty-row-actions">
        {child && quickTarget && (
          <button
            type="button"
            className={`ty-btn ty-btn--ghost ty-btn--sm ty-routine-next ${tickModifier}`}
            title={`Mark as ${statusMeta('task', quickTarget).label}`}
            aria-label={`Mark “${task.title}” as ${statusMeta('task', quickTarget).label}`}
            disabled={busyId === task.id}
            onClick={() => onQuickAdvance(task)}
          >
            {busyId === task.id ? '…' : `✓ ${statusMeta('task', quickTarget).label}`}
          </button>
        )}
        <Link
          className="ty-btn ty-btn--ghost ty-btn--sm"
          to={buildPath.taskEdit(task.id)}
        >
          Edit
        </Link>
      </td>
    </tr>
  );
}

function RoutineClubRows({
  row,
  open,
  panelId,
  status,
  onToggle,
  quickTarget,
  tickModifier,
  busyId,
  onQuickAdvance,
}) {
  const cadence = row.cadence === 'weekly' ? 'weekly' : 'daily';
  const period = cadence === 'weekly' ? 'weeks' : 'days';
  const title = row.tasks[0]?.title || 'Routine';
  const assignees = unionAssignees(row.tasks);
  const urgency = uniformValue(row.tasks, (task) => task.urgency);
  const action = open
    ? `Hide earlier ${period} of ${title}`
    : `Show earlier ${period} of ${title}`;

  return (
    <>
      <tr className="ty-routine-club" data-testid={open ? 'routine-club-open' : 'routine-club'}>
        <td data-label="" className="ty-tick-col">
          <button
            type="button"
            className="ty-routine-toggle"
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={action}
            title={action}
            data-testid="routine-club-toggle"
            onClick={onToggle}
          >
            {open ? '−' : '+'}
          </button>
        </td>
        <td data-label="Title">
          <button type="button" className="ty-routine-club__title" onClick={onToggle}>
            {title}
          </button>
          <span className="ty-routine-meta">{clubMetaLabel(cadence, row.tasks.length)}</span>
        </td>
        <td data-label="Assigned To">
          {assignees.length > 0 ? (
            <AvatarGroup people={assignees} size={26} max={4} />
          ) : (
            <span style={{ color: 'var(--ty-muted)' }}>—</span>
          )}
        </td>
        <td data-label="Urgency">{urgency == null ? 'Mixed' : (urgency || '—')}</td>
        <td data-label="End">{clubEndLabel(row.tasks)}</td>
        <td data-label="Status">
          <StatusBadge domain="task" value={status} />
        </td>
        <td data-label="" className="ty-row-actions" />
      </tr>
      {open && row.tasks.map((task, index) => (
        <TaskDataRow
          key={`${row.key}:${task.id}`}
          task={task}
          child
          cadence={cadence}
          rowId={index === 0 ? panelId : undefined}
          quickTarget={quickTarget}
          tickModifier={tickModifier}
          busyId={busyId}
          onQuickAdvance={onQuickAdvance}
        />
      ))}
    </>
  );
}

export function TaskStatusBoard({
  grouped,
  visibleStatuses,
  sort,
  onSort,
  busyId,
  onQuickAdvance,
}) {
  const [openClubs, setOpenClubs] = useState(() => new Set());

  function toggleClub(key) {
    setOpenClubs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return visibleStatuses.map((status) => {
    const taskRows = grouped[status] || [];
    if (taskRows.length === 0) return null;

    const meta = statusMeta('task', status);
    const quickTarget = QUICK_NEXT[status];
    const tickModifier =
      status === 'assigned'    ? 'ty-tick--yellow' :
      status === 'in_progress' ? 'ty-tick--green'  : '';
    const display = clubRoutineRows(taskRows);

    return (
      <section key={status} className="ty-status-section">
        <h3 className="ty-status-heading">
          <span
            className="ty-status-dot"
            style={{ backgroundColor: meta.colour }}
          />
          {meta.label}
          <span className="ty-status-count">{taskRows.length}</span>
        </h3>

        <div className="ty-table-wrap">
          <table className="ty-table">
            <thead>
              <tr>
                <th className="ty-tick-col" />
                <SortableTh label="Title" sortKey="title" activeKey={sort.key} direction={sort.dir} onSort={onSort} />
                <SortableTh label="Assigned To" sortKey="assignees" activeKey={sort.key} direction={sort.dir} onSort={onSort} />
                <SortableTh label="Urgency" sortKey="urgency" activeKey={sort.key} direction={sort.dir} onSort={onSort} />
                <SortableTh label="End Date" sortKey="end_date" activeKey={sort.key} direction={sort.dir} onSort={onSort} />
                <SortableTh label="Status" sortKey="status" activeKey={sort.key} direction={sort.dir} onSort={onSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {display.map((row) => (
                row.kind === 'club' ? (
                  <RoutineClubRows
                    key={row.key}
                    row={row}
                    open={openClubs.has(row.key)}
                    panelId={clubDomId(row.key)}
                    status={status}
                    onToggle={() => toggleClub(row.key)}
                    quickTarget={quickTarget}
                    tickModifier={tickModifier}
                    busyId={busyId}
                    onQuickAdvance={onQuickAdvance}
                  />
                ) : (
                  <TaskDataRow
                    key={row.task.id}
                    task={row.task}
                    quickTarget={quickTarget}
                    tickModifier={tickModifier}
                    busyId={busyId}
                    onQuickAdvance={onQuickAdvance}
                  />
                )
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  });
}

function taskSortValue(task, key) {
  if (key === 'assignees') {
    return (task.assignees || []).map((a) => a.full_name || a.email || '').join(', ');
  }
  return task?.[key];
}

/** Tasks list — bifurcated per status with a quick "tick" fast-forward. */
export default function TasksList() {
  const { filter = 'my_tasks' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const solo = isIndividualUser(user);
  const filters = solo
    ? TASK_FILTERS.filter((f) => f.value !== 'my_team_tasks')
    : TASK_FILTERS;

  useEffect(() => {
    if (solo && filter === 'my_team_tasks') {
      navigate(buildPath.tasksList('my_tasks'), { replace: true });
    }
  }, [solo, filter, navigate]);

  // The status filter lives in the query string rather than in component
  // state so the dashboard's stat tiles can link straight to a single
  // status (``…/task-list/my_tasks?status=in_progress``) and land on the
  // same view the dropdown would produce.  It also makes the filtered
  // view bookmarkable and survives a refresh / back button, which local
  // state silently threw away.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';
  const setStatusFilter = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value); else next.delete('status');
    // ``replace`` so flicking through statuses doesn't stack up history
    // entries the user then has to click Back through.
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // id of the task whose status is currently being updated via the tick
  // button — used to disable the control and show a busy state so a
  // double-click can't fire two transitions.
  const [busyId, setBusyId] = useState(null);
  const [sort, setSort] = useState({ key: 'end_date', dir: 'asc' });

  const apiParams = useMemo(() => {
    // Wire each filter dropdown value to the corresponding backend
    // query-string flag exposed by ``TaskViewSet.get_queryset``:
    //   * my_tasks         -> mine=1            (assigned to me)
    //   * my_team_tasks    -> team=1            (assigned to my reports)
    //   * my_created_tasks -> created_by_me=1   (I created these)
    //
    // ``ordering: 'end_date'`` asks the backend to sort by due date in
    // ascending order (nearest deadline first).  We also re-sort each
    // status group client-side (see ``grouped`` below) so the ordering
    // holds even if the API ever changes its default.
    const params = { ordering: 'end_date' };
    if (filter === 'my_tasks') params.mine = 1;
    if (filter === 'my_team_tasks') {
      params.team = 1;
      // "Tasks Assigned To My Team" hides tasks that are assigned to
      // ME (a manager's own tasks live under "My Tasks", not here) —
      // see the ``exclude_mine`` flag on ``TaskViewSet.get_queryset``.
      params.exclude_mine = 1;
    }
    if (filter === 'my_created_tasks') params.created_by_me = 1;
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    return params;

  }, [filter, search, statusFilter]);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // The bifurcated view splits EVERY task across five status tables,
    // so we must have the complete set — not just the first DRF page
    // (``PAGE_SIZE = 20`` server-side, with no page-size override param).
    // Walk the paginated response page-by-page and accumulate the rows;
    // a non-paginated array response short-circuits after one call.
    async function fetchAllTasks() {
      const all = [];
      let page = 1;
      // Hard cap the loop so a runaway ``next`` link can never spin
      // forever — 500 pages × 20 = 10k tasks is far beyond any real list.
      for (let guard = 0; guard < 500; guard += 1) {
        const data = await TasksAPI.list({ ...apiParams, page });
        if (Array.isArray(data)) {           // pagination disabled → plain array
          all.push(...data);
          break;
        }
        all.push(...(data.results || []));
        if (!data.next) break;               // last page reached
        page += 1;
      }
      return all;
    }

    fetchAllTasks()
      .then((rows) => { if (!cancelled) setTasks(rows); })
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [apiParams]);


  // Bucket the flat task list into one array per status. Default sort
  // is due date (nearest first); clicking a column heading re-sorts
  // every visible status group the same way.
  const grouped = useMemo(() => {
    const buckets = {};
    for (const s of STATUS_ORDER) buckets[s] = [];
    for (const t of tasks) {
      if (buckets[t.status]) buckets[t.status].push(t);
    }
    for (const s of STATUS_ORDER) {
      buckets[s] = sortRows(buckets[s], {
        dir: sort.dir,
        getValue: (t) => taskSortValue(t, sort.key || 'end_date'),
      });
    }
    return buckets;
  }, [tasks, sort]);

  const visibleStatuses = statusFilter ? [statusFilter] : STATUS_ORDER;
  const totalVisible = visibleStatuses.reduce(
    (sum, s) => sum + (grouped[s] ? grouped[s].length : 0),
    0,
  );

  /**
   * Fast-forward a task's status by one step via the tick button.
   * Updates the row in place on success so it hops to the correct table
   * without a full refetch.
   */
  async function quickAdvance(task) {
    const next = QUICK_NEXT[task.status];
    if (!next || busyId) return;
    setBusyId(task.id);
    try {
      await TasksAPI.setStatus(task.id, next);
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: next } : t)),
      );
      notify.success(
        `“${task.title}” moved to ${statusMeta('task', next).label}.`,
      );
    } catch (e) {
      // The axios interceptor already surfaces the backend error as a
      // toast; nothing more to do here.
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <PageHeader
        title={FILTER_TITLES[filter] || 'Tasks'}
        subtitle={
          solo
            ? 'View and manage tasks assigned to you.'
            : 'View and manage tasks assigned to you and your team.'
        }
        actions={<Link className="ty-btn" to="/task/create-task">+ Create Task</Link>}
      />

      <div className="ty-filters">
        <select
          className="ty-select"
          value={filter}
          onChange={(e) => navigate(buildPath.tasksList(e.target.value))}
        >
          {filters.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>

        <select
          className="ty-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {TASK_STATUS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>

        <input
          className="ty-input"
          placeholder="Search by title or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <ErrorBox error={error} />

      {loading ? (
        <Loader />
      ) : totalVisible === 0 ? (
        <div className="ty-empty">No tasks match your filters.</div>
      ) : (
        // One table per status.  The newest daily or weekly cron
        // occurrence stays on its own row.  Earlier copies of that
        // routine share a row; the + on the left opens each day or
        // week.  Completed copies are clubbed only inside Completed.
        <TaskStatusBoard
          grouped={grouped}
          visibleStatuses={visibleStatuses}
          sort={sort}
          onSort={(key) => setSort((prev) => nextSortState(prev, key))}
          busyId={busyId}
          onQuickAdvance={quickAdvance}
        />
      )}
    </>
  );
}
